import { useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTikTokShopConnectionStatus } from "@/api/tiktok-shop";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { PermissionsList } from "./permissions-list";
import { PromoVisual } from "./promo-visual";
import { ReconnectCallout } from "./reconnect-callout";

interface TikTokSellerConnectionsModalProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const TikTokSellerConnectionsModal = ({
  open: controlledOpen,
  onOpenChange,
}: TikTokSellerConnectionsModalProps) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { status?: string };
  const { data: shopStatus } = useTikTokShopConnectionStatus();

  // Use controlled or uncontrolled state
  const isOpen = controlledOpen ?? internalOpen;
  const setIsOpen = onOpenChange ?? setInternalOpen;

  // Clean up OAuth callback URL params
  useEffect(() => {
    if (search.status === "success") {
      navigate({ to: ".", search: undefined, replace: true });
    }
  }, [search.status, navigate]);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      navigate({ to: ".", search: undefined, replace: true });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-[656px] p-3xl gap-0 overflow-hidden [&>button]:right-9 [&>button]:top-8 [&>button>svg]:stroke-fg-quaternary">
        {/* Header — photo + TikTok logo, matching the Figma-updated design. */}
        <div
          className="relative overflow-hidden rounded-t-lg px-6 pt-6 pb-8 min-h-[181px] rounded-md flex"
          style={{ backgroundImage: "linear-gradient(173deg, rgb(27, 21, 37) 14.5%, rgb(42, 30, 50) 89.5%)" }}
        >
          <PromoVisual variant="banner" />

          {/* Title and description */}
          <div className="z-10 flex flex-col justify-end relative">
            <DialogTitle className="text-2xl font-medium text-white">TikTok Seller Connections</DialogTitle>
            <DialogDescription className="text-sm text-white/70">
              Complete the required permissions below to continue.
            </DialogDescription>
          </div>
        </div>

        <div className="mt-8 flex flex-col gap-8">
          {!shopStatus?.connected && <ReconnectCallout />}
          <PermissionsList enabled={isOpen} />
        </div>
      </DialogContent>
    </Dialog>
  );
};
